import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  getScorecard,
  getScorecardHistory,
} from "../../BalancedScorecard/balancedScorecard.service";
import type {
  ApiScorecardFull,
  HistoryListResponse,
  TrafficLight,
} from "../../BalancedScorecard/balancedScorecard.service";

const TL_COLOR: Record<string, string> = {
  green: "#16a34a",
  yellow: "#d97706",
  red: "#dc2626",
};
const TL_BG: Record<string, string> = {
  green: "#dcfce7",
  yellow: "#fef3c7",
  red: "#fee2e2",
};

const scoreColor = (s: number | null) => {
  const n = Number(s);
  if (s === null || isNaN(n)) return "#9ca3af";
  if (n >= 70) return "#16a34a";
  if (n >= 40) return "#d97706";
  return "#dc2626";
};
const scoreBg = (s: number | null) => {
  const n = Number(s);
  if (s === null || isNaN(n)) return "#f3f4f6";
  if (n >= 70) return "#dcfce7";
  if (n >= 40) return "#fef3c7";
  return "#fee2e2";
};
type Props = { projectId?: string };

const BscCard: React.FC<Props> = ({ projectId }) => {
  const { t } = useTranslation("dashboardModals");
  const tlConf = (tl: TrafficLight) =>
    tl
      ? {
          color: TL_COLOR[tl],
          bg: TL_BG[tl],
          label:
            tl === "green"
              ? t("bscCard.status.onTrack")
              : tl === "yellow"
                ? t("bscCard.status.atRisk")
                : t("bscCard.status.critical"),
        }
      : { color: "#9ca3af", bg: "#f3f4f6", label: t("bscCard.status.noData") };
  const [scorecard, setScorecard] = useState<ApiScorecardFull | null>(null);
  const [history, setHistory] = useState<HistoryListResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    Promise.all([getScorecard(projectId), null])
      .then(async ([sc]) => {
        setScorecard(sc);
        if (sc) {
          const h = await getScorecardHistory(sc.id).catch(() => null);
          setHistory(h);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return <div style={{ padding: 24, textAlign: "center" }}><Spin size="small" /></div>;
  if (!scorecard || !history || history.snapshots.length === 0) {
    return (
      <div style={{ padding: 16, color: "#9ca3af", fontSize: 13 }}>
        {t("bscCard.noPublishedHistory")}
      </div>
    );
  }

  const trend = history.trend;
  const snapshots = history.snapshots;

  return (
    <div style={{ padding: "12px 0", fontSize: 13 }}>

      {/* ── Score over time chart ─────────────────────────────────────── */}
      <div style={{ padding: "0 16px 12px", borderBottom: "1px solid #f3f4f6" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontWeight: 600, color: "#1f2937", fontSize: 13 }}>{t("bscCard.scoreOverTime")}</span>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <span style={{ color: "#4a1c96", fontSize: 11 }}>— {t("bscCard.overall")}</span>
            {trend[0]?.domains.map((d, i) => (
              <span key={i} style={{ color: d.color, fontSize: 11 }}>— {d.name}</span>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          {/* Y-axis */}
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "flex-end", paddingBottom: 18, minWidth: 28 }}>
            {[100, 75, 50, 25, 0].map((v) => (
              <span key={v} style={{ fontSize: 10, color: "#9ca3af" }}>{v}</span>
            ))}
          </div>

          {/* Chart */}
          <div style={{ flex: 1 }}>
            <svg width="100%" height="100" viewBox="0 0 600 100" preserveAspectRatio="none" style={{ overflow: "visible" }}>
              {[0, 25, 50, 75, 100].map((y) => (
                <line key={y} x1="0" y1={y} x2="600" y2={y} stroke="#f3f4f6" strokeWidth="1" />
              ))}

              {/* Domain lines (dashed) */}
              {trend[0]?.domains.map((_, di) => {
                if (trend.length < 2) return null;
                const pts = trend.map((p, i) => {
                  const x = (i / (trend.length - 1)) * 600;
                  const y = 100 - ((Number(p.domains[di]?.domain_score) || 0) / 100) * 100;
                  return `${x.toFixed(0)},${y.toFixed(0)}`;
                }).join(" ");
                return (
                  <polyline key={di} points={pts} fill="none"
                    stroke={trend[0].domains[di]?.color ?? "#ccc"}
                    strokeWidth="1.5" strokeDasharray="4 2" strokeLinejoin="round" />
                );
              })}

              {/* Overall line (solid) */}
              {trend.length > 1 && (() => {
                const pts = trend.map((p, i) => {
                  const x = (i / (trend.length - 1)) * 600;
                  const y = 100 - ((Number(p.overall_score) || 0) / 100) * 100;
                  return `${x.toFixed(0)},${y.toFixed(0)}`;
                }).join(" ");
                return <polyline points={pts} fill="none" stroke="#4a1c96" strokeWidth="2.5" strokeLinejoin="round" />;
              })()}

              {/* Dots */}
              {trend.map((p, i) => {
                const x = trend.length > 1 ? (i / (trend.length - 1)) * 600 : 300;
                const y = 100 - ((Number(p.overall_score) || 0) / 100) * 100;
                return <circle key={i} cx={x} cy={y} r="4" fill="#4a1c96" />;
              })}
            </svg>

            {/* X-axis */}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              {trend.map((p, i) => (
                <span key={i} style={{ fontSize: 10, color: "#9ca3af" }}>
                  {dayjs(p.published_at).format("MMM D")}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Snapshots table ───────────────────────────────────────────── */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {[
                t("bscCard.publishedHeader"),
                t("bscCard.scoreHeader"),
                t("bscCard.statusHeader"),
                ...snapshots[0].domains.map((d) => d.name),
              ].map((h, i) => (
                <th key={i} style={{
                  padding: "8px 12px", textAlign: "left", fontSize: 12, fontWeight: 500,
                  color: i >= 3
                    ? (scorecard?.domains.find((dom) => dom.name === snapshots[0].domains[i - 3]?.name)?.color ?? "#9ca3af")
                    : "#9ca3af",
                  borderBottom: "1px solid #f3f4f6", whiteSpace: "nowrap",
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {snapshots.map((snap) => {
              const sc = tlConf(snap.overall_traffic_light);
              return (
                <tr key={snap.id} style={{ borderBottom: "1px solid #f9fafb" }}>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                    <div style={{ fontWeight: 500, color: "#1f2937", fontSize: 12 }}>
                      {dayjs(snap.published_at).format("MMM D, YYYY")}
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>
                      {t("bscCard.publishedBy", { name: snap.published_by.name })}
                    </div>
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    <span style={{ fontWeight: 700, color: scoreColor(snap.overall_score), fontSize: 13 }}>
                      {snap.overall_score ?? "—"}
                    </span>
                  </td>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                    <span style={{ background: sc.bg, color: sc.color, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
                      {snap.overall_score} · {sc.label}
                    </span>
                  </td>
                  {snap.domains.map((d, di) => (
                    <td key={di} style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ background: scoreBg(d.domain_score), color: scoreColor(d.domain_score), borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
                          {d.domain_score ?? "—"}
                        </span>
                        {d.delta !== null && (
                          <span style={{ fontSize: 11, color: d.delta > 0 ? "#16a34a" : d.delta < 0 ? "#dc2626" : "#9ca3af" }}>
                            {d.delta > 0 ? "▲" : d.delta < 0 ? "▼" : "—"}{Math.abs(d.delta)}
                          </span>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BscCard;
